import { FacturaVenta, FacturaVentaDetalle, resolveDetalleMasterKeys } from '../services/facturaVentaService';
import ubicacionService, { etiquetaUbicacionSeleccion } from '../services/ubicacionService';
import type {
  NotaCreditoCreateDto,
  NotaCreditoFormularioCreateDto,
  NotaCreditoItemCreateDto,
} from '../services/notaCreditoVentaService';
import { normalizeItemNotas, parseLocalDate, toLocalDateStringYMD } from './formatters';
import { cantidadUmIngresoFromSnapshot } from './umDocumentoDisplay';
import {
  autoAsignarLoteUnico,
  extractLoteFlagsFromProducto,
  mapLotesComponentesPayload,
  mapLotesFromApiRaw,
  productoControlaLote,
} from './loteLineaHelpers';
import productoService from '../services/productoService';
import { POSCliente, POSLinea } from '../pages/facturaventa/pos/types';
import { resolverNombreCliente } from '../pages/facturaventa/pos/clienteNombre';

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const calcularSubtotalLinea = (l: Pick<POSLinea, 'cantidad' | 'precioUnitario' | 'descuento'>): number => {
  const base = l.cantidad * l.precioUnitario;
  return Math.round(base * (1 - l.descuento / 100) * 100) / 100;
};

export interface NotaCreditoFacturaTotalesRef {
  subtotal?: number;
  importeSinIva?: number;
  ivainscripto?: number;
  ivanoInscripto?: number;
  ivaotro?: number;
  exento?: number;
  importeTotal?: number;
}

export interface NotaCreditoPosCabecera {
  idCompania: string;
  nroSuc: number;
  fechaEmision: string;
  formaDePago: string;
  ordenCompra: string;
  transporte: string;
  ubicacion: string;
  observaciones: string;
  nroFormularioFactura: number;
}

export interface NotaCreditoPosState {
  posCliente: POSCliente | null;
  lineas: POSLinea[];
  cabecera: NotaCreditoPosCabecera;
  facturaTotales?: NotaCreditoFacturaTotalesRef;
  tituloFactura?: string;
}

function getNroUbicacionOrigen(factura: FacturaVenta & { nroUbicacionOrigen?: number }): number | undefined {
  const nro = (factura as unknown as Record<string, unknown>).nroUbicacionOrigen;
  if (nro !== undefined && nro !== null && Number(nro) > 0) {
    return Number(nro);
  }
  const ubicacionStr = factura.ubicacion;
  if (typeof ubicacionStr === 'string' && ubicacionStr.trim()) {
    const match = ubicacionStr.match(/\((\d+)\)/);
    if (match?.[1]) return Number(match[1]);
  }
  return undefined;
}

function clampPorcDesc(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

function resolvePorcDesc(d: FacturaVentaDetalle): number {
  if (d.porcDesc != null && Number(d.porcDesc) > 0) {
    return clampPorcDesc(Number(d.porcDesc));
  }
  const precioLista = Number(d.precioLista ?? d.precioUnitario ?? 0);
  const descMonto = Number(d.descuento ?? 0);
  if (descMonto > 0 && precioLista > 0) {
    return clampPorcDesc((descMonto / precioLista) * 100);
  }
  return 0;
}

function resolvePrecioLista(d: FacturaVentaDetalle): number {
  if (d.precioLista != null && Number(d.precioLista) > 0) {
    return Number(d.precioLista);
  }
  const neto = Number(d.precioUnitario ?? 0);
  const porc = resolvePorcDesc(d);
  if (porc > 0 && neto > 0) {
    return Math.round((neto / (1 - porc / 100)) * 100) / 100;
  }
  return neto;
}

const padComprobanteNum = (value: unknown, len: number) => {
  const n = Number(value);
  if (Number.isFinite(n)) return String(n).padStart(len, '0');
  return String(value ?? '').padStart(len, '0');
};

export function formatTituloFacturaOrigen(f: FacturaVenta): string {
  const letra = String(f.letra ?? '').trim().toUpperCase();
  const ns = f.nroSuc;
  const nc = f.nroComprobante;
  if (letra && ns != null && nc != null) {
    return `${letra}-${padComprobanteNum(ns, 5)}-${padComprobanteNum(nc, 8)}`;
  }
  if (f.nroFormulario) return `#${f.nroFormulario}`;
  return '';
}

export async function resolveUbicacionLabelFromFactura(
  factura: FacturaVenta & { nroUbicacionOrigen?: number },
): Promise<string> {
  let ubicacionStr = String(factura.ubicacion ?? '').trim();
  const nroUb = getNroUbicacionOrigen(factura);
  if (!ubicacionStr && nroUb) {
    try {
      const u = await ubicacionService.getUbicacion(nroUb);
      if (u) return etiquetaUbicacionSeleccion(u);
      return `Ubicación (${nroUb})`;
    } catch {
      return `Ubicación (${nroUb})`;
    }
  }
  return ubicacionStr;
}

/**
 * Hidrata estado POS desde una factura de venta (NC desde factura).
 */
export async function facturaToNotaCreditoPosState(
  factura: FacturaVenta & { nroUbicacionOrigen?: number; formaEntrega?: string; ordenDeCompra?: string },
): Promise<NotaCreditoPosState> {
  const f = factura as unknown as Record<string, unknown>;
  const ubicacion = await resolveUbicacionLabelFromFactura(factura);
  const tipoIvaFactura = String(f.tipoIVA ?? f.tipoIva ?? '').trim();
  const cuitFactura = factura.cuit ? String(factura.cuit).trim() : '';
  const nmFactura =
    resolverNombreCliente(factura) || String(factura.nombre ?? '').trim() || `Cliente #${factura.nroCliente ?? 0}`;

  const posCliente: POSCliente = {
    nroCliente: Number(factura.nroCliente ?? 0),
    nombre: nmFactura,
    razonSocial: nmFactura,
    cuit: cuitFactura || undefined,
    tipoIva: tipoIvaFactura || undefined,
    condVta: factura.formaDePago ? String(factura.formaDePago).trim() : undefined,
    nroLista: factura.nroLista ? String(factura.nroLista).trim() : undefined,
    nroVendedor:
      factura.nroVendedor != null && Number(factura.nroVendedor) > 0 ? Number(factura.nroVendedor) : undefined,
    vendedorInfo:
      factura.usuario || factura.nroVendedor != null
        ? { nroVendedor: Number(factura.nroVendedor ?? 0), nombreVendedor: factura.usuario ?? undefined }
        : undefined,
    direccionEnvio: String(f.direccionEnvio ?? f.DireccionEnvio ?? '').trim() || undefined,
    codigoDireccionEnvio: String(f.codigoDireccionEnvio ?? f.CodigoDireccionEnvio ?? '').trim() || undefined,
  };

  const detalles = factura.detalles || [];
  const codigos = Array.from(
    new Set(
      detalles
        .map((d) => String(d.codigoArticulo ?? '').trim())
        .filter((c) => c.length > 0),
    ),
  );
  const fetchedProducts: Record<string, unknown> = {};
  await Promise.all(
    codigos.map(async (codigo) => {
      try {
        const producto = await productoService.getProducto(codigo);
        if (producto) fetchedProducts[codigo] = producto;
      } catch {
        /* sin producto */
      }
    }),
  );

  const lineas: POSLinea[] = detalles.map((d: FacturaVentaDetalle) => {
    const porcDesc = resolvePorcDesc(d);
    const ingreso = cantidadUmIngresoFromSnapshot(d, 'fac');
    const precioUnitario = ingreso.precioUnitario ?? resolvePrecioLista(d);
    const cantidad = ingreso.cantidad;
    const codigo = String(d.codigoArticulo ?? '').trim();
    const master = resolveDetalleMasterKeys(d, {
      fallbackFormulario: factura.nroFormulario,
      fallbackItem: d.nroItem,
    });
    const loteFlags = extractLoteFlagsFromProducto(fetchedProducts[codigo]);
    const dRec = d as unknown as Record<string, unknown>;
    const lotesOrigen = mapLotesFromApiRaw(dRec.lotes ?? dRec.Lotes);
    const controla = productoControlaLote(loteFlags.controlLoteSerie);
    const lotesAuto =
      controla && lotesOrigen.length === 1
        ? autoAsignarLoteUnico(cantidad, lotesOrigen)
        : undefined;
    const linea: POSLinea = {
      id: genId(),
      codigoArticulo: codigo,
      descripcionArticulo: String(d.descripcionArticulo ?? '').trim(),
      cantidad,
      precioUnitario,
      descuento: porcDesc,
      subtotal: 0,
      um: ingreso.um || String(d.um ?? '').trim(),
      ...(d.cantidadUmfac != null ? { cantidadUmfac: d.cantidadUmfac } : {}),
      ...(d.umfac ? { umfac: d.umfac } : {}),
      ...(d.equivUmfac != null ? { equivUmfac: d.equivUmfac } : {}),
      ...(d.precioUnitarioUmfac != null ? { precioUnitarioUmfac: d.precioUnitarioUmfac } : {}),
      notas: d.notas ? String(d.notas) : undefined,
      controlLoteSerie: loteFlags.controlLoteSerie,
      requiereVencimiento: loteFlags.requiereVencimiento,
      ...(lotesOrigen.length > 0 ? { lotesOrigenFactura: lotesOrigen } : {}),
      ...(lotesAuto ? { lotes: lotesAuto } : {}),
      ...(master
        ? { nroFormularioMaster: master.nroFormularioMaster, nroItemMaster: master.nroItemMaster }
        : {}),
    };
    linea.subtotal = calcularSubtotalLinea(linea);
    return linea;
  });

  const idCompaniaFactura = String(
    factura.idCompania ?? f.IdCompania ?? (factura as { idCompania?: unknown }).idCompania ?? '',
  ).trim();

  const cabecera: NotaCreditoPosCabecera = {
    idCompania: idCompaniaFactura,
    nroSuc: Number(factura.nroSuc ?? 0),
    fechaEmision: toLocalDateStringYMD(),
    formaDePago: String(factura.formaDePago ?? '').trim(),
    ordenCompra: String(f.ordenDeCompra ?? factura.ordenCompra ?? '').trim(),
    transporte: String(f.formaEntrega ?? (factura as { transporte?: string }).transporte ?? '').trim(),
    ubicacion,
    observaciones: String(factura.descripcion ?? '').trim(),
    nroFormularioFactura: Number(factura.nroFormulario ?? 0),
  };

  return {
    posCliente,
    lineas,
    cabecera,
    tituloFactura: formatTituloFacturaOrigen(factura),
    facturaTotales: {
      subtotal: factura.subtotal,
      importeSinIva: factura.importeSinIva,
      ivainscripto: factura.ivainscripto,
      ivanoInscripto: factura.ivanoInscripto,
      ivaotro: factura.ivaotro,
      exento: factura.exento,
      importeTotal: factura.importeTotal,
    },
  };
}

export function parseNroUbicacionFromLabel(ubicacion: string): number | undefined {
  const m = String(ubicacion ?? '').trim().match(/\((\d+)\)\s*$/);
  const n = m ? Number(m[1]) : NaN;
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

const NOTA_CREDITO_ITEM_NOTAS_MAX = 500;

export interface NotaCreditoPosSubmitInput {
  posCliente: POSCliente | null;
  lineas: POSLinea[];
  cabecera: NotaCreditoPosCabecera;
}

export function notaCreditoPosToCreateDto(input: NotaCreditoPosSubmitInput): NotaCreditoCreateDto {
  const { posCliente, lineas, cabecera } = input;
  if (!posCliente?.nroCliente) {
    throw new Error('Cliente requerido');
  }

  const nroUbicacionOrigen = parseNroUbicacionFromLabel(cabecera.ubicacion);
  const fecha = parseLocalDate(cabecera.fechaEmision.trim());
  const fechaISO = fecha.toISOString();
  const tipoIva = String(posCliente.tipoIva ?? '').trim();

  const formulario: NotaCreditoFormularioCreateDto = {
    idCompania: cabecera.idCompania.trim(),
    descripcion: cabecera.observaciones.trim() || undefined,
    nroSuc: cabecera.nroSuc > 0 ? cabecera.nroSuc : undefined,
    fechaEmision: fechaISO,
    fechaVencim: fechaISO,
    nroCliente: posCliente.nroCliente,
    nombre: String(posCliente.nombre ?? '').trim() || undefined,
    nroLista: posCliente.nroLista ? String(posCliente.nroLista).trim() : undefined,
    formaDePago: cabecera.formaDePago.trim() || undefined,
    nroUbicacionOrigen,
    ordenDeCompra: cabecera.ordenCompra.trim() || undefined,
    formaEntrega: cabecera.transporte.trim() || undefined,
    nroFormularioFactura: cabecera.nroFormularioFactura > 0 ? cabecera.nroFormularioFactura : undefined,
    nroVendedor:
      posCliente.nroVendedor != null && posCliente.nroVendedor > 0 ? posCliente.nroVendedor : undefined,
    ...(tipoIva ? { tipoIva } : {}),
    ...(posCliente.cuit?.trim() ? { cuit: posCliente.cuit.trim() } : {}),
  };

  const items: NotaCreditoItemCreateDto[] = lineas
    .filter((l) => String(l.codigoArticulo ?? '').trim() && Number(l.cantidad) > 0)
    .map((l) => {
      const porcDesc = clampPorcDesc(Number(l.descuento ?? 0));
      return {
        codigoArticulo: String(l.codigoArticulo).trim(),
        descripcion: l.descripcionArticulo?.trim() || undefined,
        cantidad: Number(l.cantidad) || 0,
        precioUnitario: Number(l.precioUnitario) || 0,
        ...(porcDesc > 0 ? { porcDesc } : {}),
        ...(l.nroFormularioMaster != null && l.nroFormularioMaster > 0
          ? { nroFormularioMaster: l.nroFormularioMaster }
          : {}),
        ...(l.nroItemMaster != null && l.nroItemMaster > 0 ? { nroItemMaster: l.nroItemMaster } : {}),
        um: l.um?.trim() || undefined,
        notas: (() => {
          const n = normalizeItemNotas(l.notas, NOTA_CREDITO_ITEM_NOTAS_MAX);
          return n || undefined;
        })(),
        ...(l.lotes && l.lotes.length > 0 ? { lotes: l.lotes } : {}),
        ...(l.esKit
          ? { lotesComponentes: mapLotesComponentesPayload(l.lotesComponentes) }
          : {}),
      };
    });

  return { formulario, items };
}
